import type { TransactionHistoryItem } from "@developerz.ai/ggcoin-sdk";
import { createSignal, For, onCleanup, onMount, Show } from "solid-js";
import { useAuth } from "../auth/AuthContext.tsx";
import { ErrorBanner } from "../components/ErrorBanner.tsx";
import { formatMaybeDateTime, formatMoney } from "../lib/format.ts";

export function Transactions() {
  const { client } = useAuth();
  const [rows, setRows] = createSignal<TransactionHistoryItem[]>([]);
  const [nextBefore, setNextBefore] = createSignal<string | null>(null);
  const [loading, setLoading] = createSignal(true);
  const [error, setError] = createSignal<unknown | null>(null);

  async function load(before?: string) {
    setLoading(true);
    setError(null);
    try {
      const page = await client.listTransactions(before ? { before } : undefined);
      setRows((prev) => (before ? [...prev, ...page.transactions] : page.transactions));
      setNextBefore(page.nextBefore);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  onMount(() => {
    void load();
    // Live: refetch the first page when a transaction posts/reverses so the feed
    // stays current without a manual refresh. Best-effort.
    const unsubscribe = client.streamTransactions({
      onEvent: () => {
        void load();
      },
      onError: () => {},
    });
    onCleanup(() => {
      void Promise.resolve(unsubscribe()).catch(() => {});
    });
  });

  return (
    <section>
      <h1>Transactions</h1>
      <p class="muted">Newest-first ledger activity for your account, live.</p>
      <ErrorBanner error={error()} />

      <div class="table-scroll">
        <table class="table">
          <thead>
            <tr>
              <th>Kind</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Posted</th>
              <th>Transaction</th>
            </tr>
          </thead>
          <tbody>
            <Show
              when={rows().length > 0 || loading()}
              fallback={
                <tr>
                  <td colSpan={5} class="muted">
                    No transactions yet.
                  </td>
                </tr>
              }
            >
              <For each={rows()}>
                {(tx) => (
                  <tr>
                    <td>{tx.kind}</td>
                    <td class="num">{formatMoney(tx.amount)} GG</td>
                    <td>
                      <span class={`badge badge-${tx.status}`}>{tx.status}</span>
                    </td>
                    <td>{formatMaybeDateTime(tx.postedAt)}</td>
                    <td>
                      <code>{tx.id.slice(0, 12)}…</code>
                    </td>
                  </tr>
                )}
              </For>
            </Show>
          </tbody>
        </table>
      </div>

      <div class="row">
        <Show when={nextBefore()}>
          <button
            type="button"
            class="ghost"
            disabled={loading()}
            onClick={() => load(nextBefore() ?? undefined)}
          >
            {loading() ? "Loading…" : "Load more"}
          </button>
        </Show>
      </div>
    </section>
  );
}
